/**
 * TokenManager
 * Handles token refresh coordination across multiple browser tabs
 *
 * Key Features:
 * - Lock-based coordination to prevent race conditions
 * - Timeout handling for stale locks
 * - Tab-specific IDs for ownership tracking
 * - Automatic cleanup and retry logic
 * - Namespace-based isolation so CRM and portal can share the class
 *   without colliding on storage keys
 */

import {
	getWorkdItem,
	getWorkdKey,
	removeWorkdItem,
	setWorkdItem,
} from '../storage/workdStorage';
import { useAuthStore } from '../stores';

interface LockData {
	timestamp: number;
	tabId: string;
}

/**
 * Configuration for TokenManager.
 *
 * Allows callers to customize the storage namespace and the mechanism
 * used to read/write the tab ID. The CRM uses the default ('auth'
 * namespace, useAuthStore for tab ID). The portal passes its own
 * namespace and tab ID callbacks so it doesn't collide with CRM keys.
 */
export interface TokenManagerConfig {
	/**
	 * Storage namespace used for lock and auth state keys.
	 * Defaults to 'auth' (producing keys like workd:auth:refresh_lock).
	 */
	namespace?: string;

	/**
	 * Read the current tab ID. Called once during construction.
	 * Defaults to reading from useAuthStore.
	 */
	getTabId?: () => string | null;

	/**
	 * Persist a newly generated tab ID.
	 * Defaults to writing to useAuthStore.
	 */
	setTabId?: (tabId: string) => void;

	/**
	 * Extract the access token from a storage event's newValue string.
	 * Called when another tab updates the auth state key in localStorage.
	 * Defaults to parsing Zustand's persist format and reading `.accessToken`.
	 */
	parseTokenFromStorageEvent?: (newValue: string) => string | null;
}

export class TokenManager {
	private static readonly LOCK_TIMEOUT = 5000; // 5 seconds
	private static readonly STORAGE_EVENT_TIMEOUT = 6000; // Slightly longer than lock timeout

	private readonly lockKey: string;
	private readonly authStateKey: string;
	private readonly namespace: string;
	private readonly parseTokenFromStorageEvent: (newValue: string) => string | null;

	private refreshPromise: Promise<string | null> | null = null;
	private readonly tabId: string;
	private storageListeners: Array<(event: StorageEvent) => void> = [];

	constructor(config?: TokenManagerConfig) {
		this.namespace = config?.namespace ?? 'auth';
		this.lockKey = getWorkdKey(this.namespace, 'refresh_lock');
		this.authStateKey = getWorkdKey(this.namespace, 'state');

		this.parseTokenFromStorageEvent =
			config?.parseTokenFromStorageEvent ?? TokenManager.defaultParseToken;

		this.tabId = this.getOrCreateTabId(
			config?.getTabId ?? (() => useAuthStore.getState().tabId),
			config?.setTabId ?? ((id: string) => useAuthStore.getState().setTabId(id)),
		);
	}

	/**
	 * Default token parser for CRM auth — reads Zustand persist format.
	 */
	private static defaultParseToken(newValue: string): string | null {
		try {
			const parsed = JSON.parse(newValue);
			const authState = parsed.state ?? parsed;
			return authState.accessToken ?? null;
		} catch {
			return null;
		}
	}

	/**
	 * Get or create a unique tab ID for this session
	 */
	private getOrCreateTabId(
		getTabId: () => string | null,
		setTabId: (tabId: string) => void,
	): string {
		let tabId = getTabId();

		if (tabId == null) {
			tabId = `tab_${Date.now()}_${Math.random()}`;
			setTabId(tabId);
		}

		return tabId;
	}

	/**
	 * Try to acquire the refresh lock
	 * Returns true if lock was acquired, false if another tab holds it
	 */
	private tryAcquireLock(): boolean {
		const lockData = getWorkdItem<LockData>(this.namespace, 'refresh_lock');

		if (lockData != null) {
			const age = Date.now() - lockData.timestamp;

			// Lock is expired or stale
			if (age > TokenManager.LOCK_TIMEOUT) {
				console.debug('[TokenManager] Stale lock detected, taking over');
				this.acquireLock();

				return true;
			}

			// Valid lock held by another tab
			return false;
		}

		// No lock exists, acquire it
		this.acquireLock();

		return true;
	}

	/**
	 * Acquire the refresh lock
	 */
	private acquireLock(): void {
		const lockData: LockData = {
			timestamp: Date.now(),
			tabId: this.tabId,
		};

		setWorkdItem(this.namespace, 'refresh_lock', lockData);
	}

	/**
	 * Release the refresh lock (only if we own it)
	 */
	private releaseLock(): void {
		const lockData = getWorkdItem<LockData>(this.namespace, 'refresh_lock');

		if (lockData != null) {
			// Only release if we own the lock
			if (lockData.tabId === this.tabId) {
				removeWorkdItem(this.namespace, 'refresh_lock');
				console.debug('[TokenManager] Released lock');
			}
		}
	}

	/**
	 * Wait for another tab to complete the refresh
	 * Returns the new token or rejects on timeout/failure
	 */
	private waitForRefresh(): Promise<string | null> {
		if (typeof window === 'undefined') {
			throw new Error('Token refresh timeout - no coordination available');
		}

		return new Promise((resolve, reject) => {
			let resolved = false;
			let timeoutId: NodeJS.Timeout;

			// Listen for auth_state update (which contains the access token)
			const tokenHandler = (e: StorageEvent) => {
				if (
					e.key === this.authStateKey &&
					e.newValue != null &&
					!resolved
				) {
					const token = this.parseTokenFromStorageEvent(e.newValue);

					if (token != null) {
						resolved = true;
						cleanup();
						console.debug('[TokenManager] Token updated by another tab');
						resolve(token);
					}
				}
			};

			// Listen for lock release without token update (failure case)
			const lockHandler = (e: StorageEvent) => {
				if (
					e.key === this.lockKey &&
					e.newValue == null &&
					!resolved
				) {
					resolved = true;
					cleanup();
					console.debug(
						'[TokenManager] Lock released without token update, will retry',
					);
					reject(new Error('Token refresh failed in another tab'));
				}
			};

			const cleanup = () => {
				window.removeEventListener('storage', tokenHandler);
				window.removeEventListener('storage', lockHandler);

				if (timeoutId != null) {
					clearTimeout(timeoutId);
				}

				// Remove from tracking
				const tokenIndex = this.storageListeners.indexOf(tokenHandler);

				if (tokenIndex > -1) {
					this.storageListeners.splice(tokenIndex, 1);
				}

				const lockIndex = this.storageListeners.indexOf(lockHandler);

				if (lockIndex > -1) {
					this.storageListeners.splice(lockIndex, 1);
				}
			};

			// Track listeners for cleanup
			this.storageListeners.push(tokenHandler, lockHandler);

			window.addEventListener('storage', tokenHandler);
			window.addEventListener('storage', lockHandler);

			// Timeout if no response within window
			timeoutId = setTimeout(() => {
				if (!resolved) {
					resolved = true;
					cleanup();
					console.debug('[TokenManager] Wait timeout, will retry');
					reject(new Error('Token refresh timeout'));
				}
			}, TokenManager.STORAGE_EVENT_TIMEOUT);
		});
	}

	/**
	 * Coordinate token refresh across tabs
	 * Ensures only one tab refreshes at a time, others wait for result
	 */
	async coordinateRefresh(
		performRefresh: () => Promise<string | null>,
	): Promise<string | null> {
		// If refresh already in progress in this tab, wait for it
		if (this.refreshPromise != null) {
			console.debug('[TokenManager] Refresh already in progress in this tab');

			return this.refreshPromise;
		}

		// Try to acquire lock
		if (!this.tryAcquireLock()) {
			// Another tab has the lock, wait for it to complete
			console.debug('[TokenManager] Another tab is refreshing, waiting...');

			try {
				const newToken = await this.waitForRefresh();

				return newToken;
			} catch (error) {
				// Waiting failed (timeout or other tab failed), retry by acquiring lock
				console.debug('[TokenManager] Waiting failed, retrying...', error);

				return this.coordinateRefresh(performRefresh);
			}
		}

		// We have the lock, perform refresh
		console.debug('[TokenManager] Acquired lock, performing refresh');
		this.refreshPromise = this.performRefreshWithCleanup(performRefresh);

		return this.refreshPromise;
	}

	/**
	 * Perform the actual token refresh with proper cleanup
	 */
	private async performRefreshWithCleanup(
		performRefresh: () => Promise<string | null>,
	): Promise<string | null> {
		try {
			const newToken = await performRefresh();

			// The caller is responsible for persisting the new token to storage.
			// Other tabs listen for the auth state storage event and extract the new token.
			console.debug('[TokenManager] Refresh successful');

			this.releaseLock();

			return newToken;
		} catch (error) {
			console.debug('[TokenManager] Refresh failed:', error);
			this.releaseLock();

			throw error;
		} finally {
			this.refreshPromise = null;
		}
	}

	/**
	 * Cleanup method to remove storage listeners
	 */
	cleanup(): void {
		if (typeof window !== 'undefined') {
			for (const listener of this.storageListeners) {
				window.removeEventListener('storage', listener);
			}

			this.storageListeners = [];
		}

		this.releaseLock();
	}
}
